import React from "react";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import pujaData from "./pujas.json";
import {
  FaClipboardList,
  FaMagic,
  FaPray,
  FaArrowRight,
  FaDownload,
} from "react-icons/fa";
import "./PujaListBuilder.css";

const formatQuantity = (item) => {
  const count = item.count ?? 1;
  if (item.unit) {
    return `${count} ${item.unit}`;
  }
  return `${count}`;
};

const escapePdfText = (value) =>
  String(value)
    .replace(/[•]/g, "-")
    .replace(/[—–]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[^\x20-\x7E\n]/g, "")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const splitPdfText = (text, maxLength = 86) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (nextLine.length > maxLength) {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
      return;
    }
    currentLine = nextLine;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

const buildPdfDocument = (lines) => {
  const pageWidth = 612;
  const pageHeight = 792;
  const marginTop = 54;
  const marginBottom = 54;
  const lineHeight = 16;
  const pages = [];
  let currentPage = [];
  let currentY = pageHeight - marginTop;

  lines.forEach((line) => {
    if (currentY <= marginBottom) {
      pages.push(currentPage);
      currentPage = [];
      currentY = pageHeight - marginTop;
    }

    currentPage.push({
      text: line.text,
      x: line.x,
      y: currentY,
      fontSize: line.fontSize || 12
    });
    currentY -= line.lineHeight || lineHeight;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  const objects = [];

  objects.push("1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj");
  const pageRefs = pages.map((_, index) => `${3 + index * 2} 0 R`).join(" ");
  objects.push(`2 0 obj << /Type /Pages /Count ${pages.length} /Kids [${pageRefs}] >> endobj`);

  pages.forEach((pageLines, pageIndex) => {
    const pageObjectId = 3 + pageIndex * 2;
    const contentObjectId = pageObjectId + 1;
    const content = [
      "BT",
      "/F1 12 Tf",
      ...pageLines.flatMap((line) => [
        `/F1 ${line.fontSize} Tf`,
        `${line.x} ${line.y} Td`,
        `(${escapePdfText(line.text)}) Tj`,
        `${-line.x} ${-line.y} Td`
      ]),
      "ET"
    ].join("\n");

    objects.push(
      `${pageObjectId} 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${3 + pages.length * 2} 0 R >> >> /Contents ${contentObjectId} 0 R >> endobj`
    );
    objects.push(
      `${contentObjectId} 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`
    );
  });

  const fontObjectId = 3 + pages.length * 2;
  objects.push(`${fontObjectId} 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];

  objects.forEach((object) => {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return pdf;
};

const aggregateItems = (items) => {
  const merged = new Map();

  items.forEach((item) => {
    const key = item.name;
    const existing = merged.get(key);

    if (!existing) {
      merged.set(key, {
        ...item,
        count: item.count ?? 1,
        source: item.source ? [item.source] : [],
      });
      return;
    }

    merged.set(key, {
      ...existing,
      count: (existing.count ?? 0) + (item.count ?? 0),
      notes:
        existing.notes && item.notes && existing.notes !== item.notes
          ? `${existing.notes}; ${item.notes}`
          : existing.notes || item.notes,
      source: Array.from(new Set([...(existing.source || []), item.source].filter(Boolean))),
    });
  });

  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export default class PujaListBuilder extends React.Component {
  constructor() {
    super();
    this.state = {
      selectedIds: [],
      generatedItems: null,
    };
  }

  toggleDeity = (deityId) => {
    this.setState((currentState) => {
      const selectedIds = currentState.selectedIds.includes(deityId)
        ? currentState.selectedIds.filter((id) => id !== deityId)
        : [...currentState.selectedIds, deityId];

      return {
        selectedIds,
        generatedItems: null,
      };
    });
  };

  generateList = () => {
    const selectedDeities = pujaData.deities.filter((deity) =>
      this.state.selectedIds.includes(deity.id)
    );

    const deityItems = selectedDeities.flatMap((deity) =>
      deity.items.map((item) => ({
        ...item,
        source: deity.name,
      }))
    );

    const commonItems = pujaData.commonItems.map((item) => ({
      ...item,
      source: "Common",
    }));

    this.setState({
      generatedItems: aggregateItems([...commonItems, ...deityItems]),
    });
  };

  clearSelection = () => {
    this.setState({
      selectedIds: [],
      generatedItems: null,
    });
  };

  downloadPdf = () => {
    const { generatedItems, selectedIds } = this.state;
    if (!generatedItems || generatedItems.length === 0) {
      return;
    }

    const selectedDeities = pujaData.deities.filter((deity) =>
      selectedIds.includes(deity.id)
    );

    const lines = [
      { text: "Puja List", x: 54, fontSize: 20, lineHeight: 26 },
      { text: "By Pt. Vishwaram Maharaj", x: 54, fontSize: 12, lineHeight: 22 },
      { text: pujaData.meta.note, x: 54, fontSize: 10, lineHeight: 20 },
      {
        text: `Selected Pujas: ${selectedDeities.map((deity) => deity.name).join(", ")}`,
        x: 54,
        fontSize: 12,
        lineHeight: 24
      },
      { text: "Items", x: 54, fontSize: 15, lineHeight: 22 }
    ];

    generatedItems.forEach((item) => {
      splitPdfText(
        `- ${item.name} - ${formatQuantity(item)}${item.notes ? ` (${item.notes})` : ""}`,
        88
      ).forEach((line, index) => {
        lines.push({
          text: line,
          x: index === 0 ? 54 : 72,
          fontSize: 11,
          lineHeight: 16
        });
      });
    });

    const pdfContent = buildPdfDocument(lines);
    const blob = new Blob([pdfContent], { type: "application/pdf" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `puja-list-${selectedDeities
      .map((deity) => deity.name.toLowerCase().replace(/\s+/g, "-"))
      .join("-") || "custom"}.pdf`;
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
  };

  render() {
    const { selectedIds, generatedItems } = this.state;
    const selectedDeities = pujaData.deities.filter((deity) =>
      selectedIds.includes(deity.id)
    );
    const itemCount = generatedItems?.length || 0;

    return (
      <SiteLayout>
        <main className="page-shell puja-page home-v3-shell">
          <PageIntro
            eyebrow="Spirituality / Puja Builder"
            title={pujaData.meta.title}
            description={`${pujaData.meta.subtitle} ${pujaData.meta.note}`}
            visual={(
              <div className="puja-hero-panel home-v3-panel card-pad">
                <span className="home-v3-kicker">Workflow</span>
                <div className="puja-workflow-step">
                  <strong>Select</strong>
                  <p>Tap the pujas you want to include.</p>
                </div>
                <div className="puja-workflow-step">
                  <strong>Generate</strong>
                  <p>Merge the common and deity-specific materials.</p>
                </div>
                <div className="puja-workflow-step">
                  <strong>Export</strong>
                  <p>Download a clean PDF for shopping or setup.</p>
                </div>
              </div>
            )}
          />

          <section className="section puja-selection-section">
            <div className="puja-workspace">
              <div className="puja-selection-panel home-v3-panel card-pad">
                <div className="puja-section-head">
                  <div>
                    <span className="home-v3-kicker">Select Pujas</span>
                    <h2>Tap to select.</h2>
                    <p>Pick one or more pujas, then generate the combined materials list.</p>
                  </div>
                  <div className="puja-selection-meta">
                    <span className="pill">
                      <FaPray /> {selectedDeities.length} selected
                    </span>
                    <span className="pill">
                      <FaClipboardList /> {itemCount} total items
                    </span>
                  </div>
                </div>

                <div className="puja-select-grid">
                  {pujaData.deities.map((deity) => {
                    const selected = selectedIds.includes(deity.id);
                    return (
                      <button
                        key={deity.id}
                        type="button"
                        className={`puja-select-card ${selected ? "is-selected" : ""}`}
                        onClick={() => this.toggleDeity(deity.id)}
                        aria-pressed={selected}
                      >
                        <div className="puja-select-top">
                          <span className="app-badge">{deity.accent}</span>
                          <span className="puja-select-state">
                            {selected ? "Selected" : "Tap to add"}
                          </span>
                        </div>
                        <h3 className="feature-title">{deity.name}</h3>
                        <p className="meta-copy">{deity.items.length} deity-specific items</p>
                        <span className="puja-select-footer">
                          {selected ? "Remove" : "Add"} <FaArrowRight />
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="puja-actions">
                  <button
                    type="button"
                    className="home-v3-button is-primary puja-action-button"
                    onClick={this.generateList}
                    disabled={selectedIds.length === 0}
                  >
                    <FaMagic /> Generate Items
                  </button>
                  <button
                    type="button"
                    className="home-v3-button is-secondary puja-action-button"
                    onClick={this.clearSelection}
                  >
                    Clear Selection
                  </button>
                  {generatedItems ? (
                    <button
                      type="button"
                      className="home-v3-button is-secondary puja-action-button"
                      onClick={this.downloadPdf}
                    >
                      <FaDownload /> Generate PDF
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="puja-results-panel home-v3-panel card-pad">
                <div className="puja-section-head">
                  <div>
                    <span className="home-v3-kicker">Generated List</span>
                    <h2>Materials list.</h2>
                    <p>The list updates from the selected pujas and can be exported as a PDF.</p>
                  </div>
                  <div className="puja-results-summary">
                    <span className="app-badge">
                      <FaClipboardList /> {itemCount} line items
                    </span>
                  </div>
                </div>

                {selectedDeities.length > 0 ? (
                  <div className="pill-list puja-selected-list">
                    {selectedDeities.map((deity) => (
                      <span className="pill" key={deity.id}>
                        <FaPray /> {deity.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="puja-empty-state">
                    <p>Select at least one puja, then generate the consolidated list.</p>
                  </div>
                )}

                {generatedItems ? (
                  <div className="puja-results">
                    <div className="puja-results-list">
                      {generatedItems.map((item) => (
                        <div className="puja-result-row" key={item.name}>
                          <div className="puja-result-copy">
                            <div className="puja-result-name">{item.name}</div>
                            {item.notes ? (
                              <div className="meta-copy puja-result-notes">
                                {item.notes}
                              </div>
                            ) : null}
                            {item.source?.length ? (
                              <div className="puja-result-source">
                                {item.source.join(", ")}
                              </div>
                            ) : null}
                          </div>
                          <div className="pill puja-result-qty">{formatQuantity(item)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        </main>
      </SiteLayout>
    );
  }
}
